import type { PlaylistSummary, Track } from '@blackblack/shared';
import { api } from '../../lib/api';
import { useQuery } from '../../lib/query';
import { Link, navigate } from '../../lib/router';
import { useAuth } from '../../state/auth';
import { Icon } from '../../components/ui/Icon';
import { ErrorState, Skeleton } from '../../components/ui/primitives';
import { useOpenSheet } from '../../components/ui/Sheet';
import { Page, Section, Shelf } from '../../components/layout';
import { PlaylistCard } from '../playlists/PlaylistCard';
import { PlaylistEditorSheet } from '../playlists/PlaylistEditorSheet';
import { TrackCard } from './TrackCard';
import { TrackRow } from './TrackRow';
import styles from './music.module.css';

/** Muziek tab: search, your library, trending and new music. */
export function MusicPage() {
  const { me, status, requireAuth } = useAuth();
  const openSheet = useOpenSheet();
  const trending = useQuery<Track[]>('tracks:trending', () => api.page<Track>('/music/tracks', { sort: 'trending', limit: 8 }).then((p) => p.items));
  const fresh = useQuery<Track[]>('tracks:new', () => api.page<Track>('/music/tracks', { sort: 'new', limit: 12 }).then((p) => p.items));
  const mine = useQuery<PlaylistSummary[]>(me ? `playlists:user:${me.id}` : null, () => api.get(`/users/${me!.id}/playlists`));
  const followed = useQuery<PlaylistSummary[]>(me ? `playlists:followed:${me.id}` : null, () => api.get('/playlists/followed'));

  const newPlaylist = () => {
    if (!requireAuth('Log in om playlists te maken')) return;
    openSheet((close) => <PlaylistEditorSheet onClose={close} onSaved={(p) => navigate(`/playlist/${p.id}`)} />);
  };

  return (
    <Page>
      <header className={styles.musicHeader}>
        <h1>Muziek</h1>
        <button className={styles.searchBar} onClick={() => navigate('/search?type=tracks')}>
          <Icon name="search" size={18} />
          <span>Nummers, artiesten, albums…</span>
        </button>
      </header>

      <Section title="Jouw bibliotheek" action={<button className={styles.textBtn} onClick={newPlaylist}><Icon name="plus" size={16} /> Nieuwe playlist</button>}>
        <Shelf>
          <Link to={status === 'authed' ? '/library/liked' : '#'} className={styles.likedCard} onClick={(e) => status !== 'authed' && (e.preventDefault(), requireAuth('Log in om je gelikete nummers te zien'))}>
            <Icon name="heart" size={30} filled />
            <span>Gelikete nummers</span>
          </Link>
          {mine.data?.map((p) => <PlaylistCard key={p.id} playlist={p} />)}
          {followed.data?.map((p) => <PlaylistCard key={p.id} playlist={p} showOwner />)}
          <button className={styles.newCard} onClick={newPlaylist} aria-label="Nieuwe playlist">
            <Icon name="plus" size={28} />
            <span>Nieuwe playlist</span>
          </button>
        </Shelf>
      </Section>

      <Section title="Trending nummers">
        {trending.error ? (
          <ErrorState error={trending.error} onRetry={trending.refetch} />
        ) : (
          <div className={styles.rows}>
            {trending.loading
              ? [0, 1, 2, 3, 4].map((i) => <RowSkeleton key={i} />)
              : trending.data?.map((t, i) => <TrackRow key={t.id} track={t} list={trending.data} index={i} context="Trending nummers" number={i + 1} showCover />)}
          </div>
        )}
      </Section>

      <Section title="Nieuwe muziek">
        <Shelf>
          {fresh.loading
            ? [0, 1, 2, 3].map((i) => <Skeleton key={i} w={148} h={190} r={14} />)
            : fresh.data?.map((t, i) => <TrackCard key={t.id} track={t} list={fresh.data!} index={i} context="Nieuwe muziek" />)}
        </Shelf>
      </Section>
    </Page>
  );
}

export function RowSkeleton() {
  return (
    <div className={styles.rowSkeleton}>
      <Skeleton w={48} h={48} r={8} />
      <div style={{ flex: 1, display: 'grid', gap: 6 }}>
        <Skeleton w="55%" h={14} />
        <Skeleton w="35%" h={12} />
      </div>
    </div>
  );
}
